import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSpace } from '../../features/spaces/api';
import { api } from '../../api/client';

// Previously read from COMPANY_DIRECTORY, a hardcoded roster of 15 people
// with its own fake ids ('emp-shoaib', 'u-uzef', ...) — a fifth,
// independently-maintained employee list alongside the real GoodDayUser
// collection, with a real data bug (one employee listed under two
// departments). Assignee pickers across Spaces now read real users via
// GET /users/assignable instead.

export interface UserOption {
  id: string;
  name: string;
  display_name: string;
  username?: string;
  department?: string;
  role?: string;
  title?: string;
  email?: string;
  initials?: string;
  color?: string;
}

const AVATAR_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#0ea5e9', '#a855f7', '#ec4899', '#64748b']

function colorForId(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function initialsFor(name: string): string {
  return name.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase()
}

function mapAssignableUserToOption(u: any): UserOption {
  const roleSuffix = u.role === 'SUPERADMIN' ? ' (Super Admin)' : u.role === 'ADMIN' ? ' (Admin)' : ''
  return {
    id: u.id,
    name: u.name,
    display_name: `${u.name}${u.departmentRole ? ` — ${u.departmentRole}` : ''}${roleSuffix}`,
    username: u.username,
    department: u.department,
    role: u.role,
    title: u.departmentRole,
    email: u.email,
    initials: initialsFor(u.name),
    color: colorForId(u.id),
  }
}

/** Prepend the superadmin to a list (deduped) */
function withSuperadmin(list: UserOption[], allUsers: UserOption[]): UserOption[] {
  const superadmin = allUsers.find((u) => u.role === 'SUPERADMIN')
  if (!superadmin) return list
  const deduped = list.filter((u) => u.id !== superadmin.id)
  return [superadmin, ...deduped]
}

/**
 * Returns the assignees for the given space project.
 * Automatically filters by the employee roles of the department services
 * who are selected / assigned for this project.
 */
export function useUserOptions(spaceId: string | null = null) {
  const queryClient = useQueryClient();

  // Listen for real-time changes when members are assigned/unassigned in SpaceMembersModal
  useEffect(() => {
    if (!spaceId || typeof window === 'undefined') return;

    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ spaceId: string }>;
      if (customEvent.detail?.spaceId === spaceId) {
        queryClient.invalidateQueries({ queryKey: ['work-projects', 'members', spaceId] });
      }
    };

    window.addEventListener('spaceMembersUpdated', handleUpdate);
    return () => window.removeEventListener('spaceMembersUpdated', handleUpdate);
  }, [spaceId, queryClient]);

  return useQuery({
    queryKey: ['work-projects', 'members', spaceId],
    queryFn: async (): Promise<UserOption[]> => {
      const res = await api.get<any>('/users/assignable')
      const allUsers: UserOption[] = (res?.data || []).map(mapAssignableUserToOption)

      if (!spaceId) {
        return allUsers;
      }

      try {
        // Assignees are the project's members (backend is the source of truth).
        // A project with no members yet falls through to everyone.
        const space = await getSpace(spaceId);
        const validIds = new Set(allUsers.map((u) => u.id));
        const memberIds = new Set(
          (space.members ?? []).map((m) => m.userId).filter((id) => id && validIds.has(id))
        );
        if (memberIds.size > 0) {
          return withSuperadmin(allUsers.filter((u) => memberIds.has(u.id)), allUsers);
        }
      } catch (err) {
        console.warn('Error fetching space assignees, falling back to all users:', err);
      }

      return withSuperadmin(allUsers, allUsers);
    },
    enabled: true,
  });
}
